<script>
import { Overlay, Tool } from 'trading-vue-js'
import Pin from './primitives/pin.js'
import { mapGetters, mapActions } from 'vuex'
import { debounce } from "../../utils/helper";
import ToolSavingMixins from 'Tools/ToolSavingMixins'
import Math2 from './primitives/math.js'

export default {
    name: 'AnchoredVWAP',
    mixins: [Overlay, Tool, ToolSavingMixins],
    methods: {
        meta_info() {
            return { 
                author: 'Hardik Santoki', 
                version: '1.0.0', 
                desc: 'Anchored VWAP',
                preset: {
                    name: 'Anchored VWAP',
                    side: 'onchart',
                    settings: {
                        lineColor: '#2962FF',
                        lineWidth: 2,
                        lineStyle: 'solid',
                        source: 'hlc3',
                        showBand1: true,
                        showBand2: false,
                        showBand3: false,
                        band1Mult: 1.0,
                        band2Mult: 2.0,
                        band3Mult: 3.0,
                        band1Color: 'rgba(41, 98, 255, 0.15)',
                        band2Color: 'rgba(41, 98, 255, 0.15)',
                        band3Color: 'rgba(41, 98, 255, 0.15)',
                    }
                }
            }
        },
        tool() {
            let prevSettings = localStorage.getItem('AnchoredVWAP')
            if (prevSettings) {
                prevSettings = JSON.parse(prevSettings)
            }
            return {
                group: 'Lines',
                type: 'AVWAP',
                hint: 'Anchored VWAP',
                data: [],
                settings: {
                    p1: null,
                    lineColor: prevSettings?.lineColor || '#2962FF',
                    lineWidth: prevSettings?.lineWidth || 2,
                    lineStyle: prevSettings?.lineStyle || 'solid',
                    source: prevSettings?.source || 'hlc3',
                    showBand1: prevSettings?.showBand1 !== undefined ? prevSettings.showBand1 : true,
                    showBand2: prevSettings?.showBand2 !== undefined ? prevSettings.showBand2 : false,
                    showBand3: prevSettings?.showBand3 !== undefined ? prevSettings.showBand3 : false,
                    band1Mult: prevSettings?.band1Mult || 1.0,
                    band2Mult: prevSettings?.band2Mult || 2.0,
                    band3Mult: prevSettings?.band3Mult || 3.0,
                    band1Color: prevSettings?.band1Color || 'rgba(41, 98, 255, 0.15)',
                    band2Color: prevSettings?.band2Color || 'rgba(41, 98, 255, 0.15)',
                    band3Color: prevSettings?.band3Color || 'rgba(41, 98, 255, 0.15)',
                    undo: 1,
                    settingsList: [
                        'lineColor', 
                        'lineWidth', 
                        'lineStyle', 
                        'source', 
                        'showBand1', 
                        'showBand2', 
                        'showBand3', 
                        'band1Mult', 
                        'band2Mult', 
                        'band3Mult',
                        'band1Color',
                        'band2Color',
                        'band3Color',
                        'settings'
                    ],
                }
            }
        },
        init() {
            const p1 = new Pin(this, 'p1')
            p1.hidden = true
            this.pins.push(p1)
            // Single-click tool: finish immediately after placement
            this.$nextTick(() => {
                if (this.p1 && this.sett.$state !== 'finished') {
                    this.updateSettingsState(this, '0')
                    this.set_state('finished')
                    this.$emit('drawing-mode-off')
                    this.$emit('object-selected')
                }
            })
            this.registerMouseEvent(this)
        },
        draw(ctx) {
            if (!this.p1) return
            
            const layout = this.layout
            const candles = this.dc.data.chart.data
            if (!candles || candles.length === 0) return

            // 1. Find anchor index
            const anchorTime = this.p1[2]
            if (!anchorTime) return

            let startIdx = -1;
            const t2i = layout.ti_map.t2i(anchorTime);
            if (Number.isNaN(t2i)) return;

            // Optimization: start searching from an approximate index
            // We use Math.floor to ensure we have an integer index for array access
            const approxIdx = Math.max(0, Math.floor(t2i) - 2); 
            
            if (anchorTime <= candles[0][0]) {
                startIdx = 0;
            } else {
                for (let i = approxIdx; i < candles.length; i++) {
                    const candle = candles[i];
                    if (!candle) continue;

                    const nextCandle = candles[i + 1];
                    // Match TradingView: find the candle that contains the anchorTime 
                    // (candle.time <= anchorTime < nextCandle.time)
                    if (candle[0] <= anchorTime && (!nextCandle || nextCandle[0] > anchorTime)) {
                        startIdx = i;
                        break;
                    }
                }
            }
            if (startIdx === -1) return

            // 2. Calculate VWAP and Bands
            const source = this.sett.source || 'hlc3'
            const vwapData = this.calculateAVWAP(candles, startIdx, source)
            
            // 3. Render
            this.collisions = []
            this.renderAVWAP(ctx, layout, vwapData)
            
            // Sync pin position to origin for correct hover/drag behavior
            if (vwapData.length > 0) {
                const p1Pin = this.pins.find(p => p.name === 'p1')
                if (p1Pin) {
                    p1Pin.y$ = vwapData[0].vwap
                    p1Pin.y = layout.$2screen(p1Pin.y$)
                }
            }

            this.render_pins(ctx)
        },
        calculateAVWAP(candles, startIdx, source) {
            let sumV = 0
            let vwap = 0
            let M2 = 0 // For Welford's weighted variance
            const results = []

            for (let i = startIdx; i < candles.length; i++) {
                const candle = candles[i]
                const price = this.getPriceBySource(candle, source)
                const vol = candle[5] || 0
                if (vol === 0) {
                    if (results.length > 0) {
                        results.push({ t: candle[0], vwap: vwap, sd: Math.sqrt(M2 / sumV) || 0 })
                    }
                    continue
                }

                const prevSumV = sumV
                sumV += vol
                const delta = price - vwap
                vwap += (vol / sumV) * delta
                M2 += vol * delta * (price - vwap)
                
                const sd = Math.sqrt(Math.max(0, M2 / sumV))

                results.push({
                    t: candle[0],
                    vwap: vwap,
                    sd: sd
                })
            }
            return results
        },
        getPriceBySource(c, source) {
            // c format: [t, o, h, l, c, v, ..., value]
            switch (source) {
                case 'open': return c[1]
                case 'high': return c[2]
                case 'low': return c[3]
                case 'close': return c[4]
                case 'hl2': return (c[2] + c[3]) / 2
                case 'hlc3': return (c[2] + c[3] + c[4]) / 3
                case 'ohlc4': return (c[1] + c[2] + c[3] + c[4]) / 4
                default: return (c[2] + c[3] + c[4]) / 3 // fallback to hlc3
            }
        },
        renderAVWAP(ctx, layout, data) {
            if (data.length < 1) return

            const { t2screen, $2screen } = layout
            const sett = this.sett

            // --- Draw Bands (Fills) ---
            this.drawBands(ctx, layout, data)

            // --- Draw VWAP Main Line ---
            ctx.beginPath()
            ctx.lineWidth = sett.lineWidth || 2
            ctx.strokeStyle = sett.lineColor || '#2962FF'
            this.setLineStyle(ctx, sett.lineStyle)
            
            for (let i = 0; i < data.length; i++) {
                const x = t2screen(layout.ti_map.t2i(data[i].t))
                const y = $2screen(data[i].vwap)
                if (i === 0) ctx.moveTo(x, y)
                else ctx.lineTo(x, y)
            }
            ctx.stroke()
            ctx.setLineDash([]) // Reset

            // --- Draw Origin Marker ---
            if (data.length > 0) {
                const x0 = t2screen(layout.ti_map.t2i(data[0].t))
                const y0 = $2screen(data[0].vwap)
                
                ctx.beginPath()
                ctx.arc(x0, y0, 5, 0, Math.PI * 2)
                ctx.fillStyle = '#FFFFFF'
                ctx.fill()
                ctx.strokeStyle = sett.lineColor || '#2962FF'
                ctx.lineWidth = 2
                ctx.stroke()
            }

            // --- Add Collisions ---
            this.collisions.push((x, y) => {
                const tolerance = this.$props.config.TOOL_COLL || 5
                const sett = this.sett
                const activeBands = []
                if (sett.showBand1 && sett.band1Mult) activeBands.push(sett.band1Mult)
                if (sett.showBand2 && sett.band2Mult) activeBands.push(sett.band2Mult)
                if (sett.showBand3 && sett.band3Mult) activeBands.push(sett.band3Mult)

                for (let i = 1; i < data.length; i++) {
                    const t1i = layout.ti_map.t2i(data[i-1].t)
                    const t2i = layout.ti_map.t2i(data[i].t)
                    const scX1 = t2screen(t1i)
                    const scX2 = t2screen(t2i)

                    // Middle line
                    const p1_mid = [scX1, $2screen(data[i-1].vwap)]
                    const p2_mid = [scX2, $2screen(data[i].vwap)]
                    if (Math2.point2seg([x, y], p1_mid, p2_mid) < tolerance) return true

                    // Bands
                    for (const mult of activeBands) {
                        // Upper line
                        const p1_up = [scX1, $2screen(data[i-1].vwap + data[i-1].sd * mult)]
                        const p2_up = [scX2, $2screen(data[i].vwap + data[i].sd * mult)]
                        if (Math2.point2seg([x, y], p1_up, p2_up) < tolerance) return true

                        // Lower line
                        const p1_low = [scX1, $2screen(data[i-1].vwap - data[i-1].sd * mult)]
                        const p2_low = [scX2, $2screen(data[i].vwap - data[i].sd * mult)]
                        if (Math2.point2seg([x, y], p1_low, p2_low) < tolerance) return true
                    }
                }
                return false
            })
        },
        drawBands(ctx, layout, data) {
            const { t2screen, $2screen } = layout
            const sett = this.sett
            
            ctx.save()
            ctx.lineJoin = 'round'
            
            // 1. Draw Fills FIRST (Outermost to Innermost)
            // if (sett.showBand3 && sett.band3Mult) this.fillBand(ctx, layout, data, sett.band3Mult, sett.band3Color)
            // if (sett.showBand2 && sett.band2Mult) this.fillBand(ctx, layout, data, sett.band2Mult, sett.band2Color)
            // if (sett.showBand1 && sett.band1Mult) this.fillBand(ctx, layout, data, sett.band1Mult, sett.band1Color)

            // 2. Draw Lines SECOND (On top of fills)
            ctx.lineWidth = 1
            this.setLineStyle(ctx, 'solid')

            if (sett.showBand1 && sett.band1Mult) {
                const color = sett.band1Color || 'rgba(41, 98, 255, 0.15)'
                ctx.strokeStyle = color.replace('0.15', '0.3').replace('0.1', '0.3')
                this.drawBandLinesOnly(ctx, layout, data, sett.band1Mult)
            }
            if (sett.showBand2 && sett.band2Mult) {
                const color = sett.band2Color || 'rgba(41, 98, 255, 0.15)'
                ctx.strokeStyle = color.replace('0.15', '0.3').replace('0.1', '0.3')
                this.drawBandLinesOnly(ctx, layout, data, sett.band2Mult)
            }
            if (sett.showBand3 && sett.band3Mult) {
                const color = sett.band3Color || 'rgba(41, 98, 255, 0.15)'
                ctx.strokeStyle = color.replace('0.15', '0.3').replace('0.1', '0.3')
                this.drawBandLinesOnly(ctx, layout, data, sett.band3Mult)
            }
            
            ctx.restore()
        },
        drawBandLinesOnly(ctx, layout, data, mult) {
            const { t2screen, $2screen } = layout
            // Upper
            ctx.beginPath()
            for (let i = 0; i < data.length; i++) {
                const x = t2screen(layout.ti_map.t2i(data[i].t))
                const y = $2screen(data[i].vwap + data[i].sd * mult)
                if (i === 0) ctx.moveTo(x, y)
                else ctx.lineTo(x, y)
            }
            ctx.stroke()
            // Lower
            ctx.beginPath()
            for (let i = 0; i < data.length; i++) {
                const x = t2screen(layout.ti_map.t2i(data[i].t))
                const y = $2screen(data[i].vwap - data[i].sd * mult)
                if (i === 0) ctx.moveTo(x, y)
                else ctx.lineTo(x, y)
            }
            ctx.stroke()
        },
        fillBand(ctx, layout, data, mult, color) {
            const { t2screen, $2screen } = layout
            ctx.beginPath()
            ctx.fillStyle = color
            
            // Forward (Upper)
            for (let i = 0; i < data.length; i++) {
                const x = t2screen(layout.ti_map.t2i(data[i].t))
                const y = $2screen(data[i].vwap + data[i].sd * mult)
                if (i === 0) ctx.moveTo(x, y)
                else ctx.lineTo(x, y)
            }
            // Backward (Lower)
            for (let i = data.length - 1; i >= 0; i--) {
                const x = t2screen(layout.ti_map.t2i(data[i].t))
                const y = $2screen(data[i].vwap - data[i].sd * mult)
                ctx.lineTo(x, y)
            }
            ctx.closePath()
            ctx.fill()
        },
        setLineStyle(ctx, style) {
            switch (style) {
                case 'dashed': ctx.setLineDash([5, 5]); break
                case 'dotted': ctx.setLineDash([2, 2]); break
                default: ctx.setLineDash([]); break
            }
        },
        use_for() {
            return ['AnchoredVWAP']
        },
        data_colors() {
            return [this.sett.lineColor]
        },
        ...mapActions('action', ['saveUndoAction'])
    },
    computed: {
        ...mapGetters([
            'themePrimaryColor',
            'themeSecondaryColor',
            'themeFontColor',
            'timeFrame',
            'dc'
        ]),
        ...mapGetters('action', [
            'currentUndoValue',
            'currentRef'
        ]),
        undo() {
            return this.$props.settings.undo
        },
        sett() {
            return this.$props.settings
        },
        p1() {
            return this.$props.settings.p1
        }
    },
    watch: {
        undo(newVal, oldVal) {
            if (newVal != oldVal) {
                if (this.$props.settings?.$uuid == this.currentRef?.drawingRef) {
                    this.pins.forEach((x, i) => x.update_from(
                        this.currentUndoValue[x.name], true
                    ))
                    this.$nextTick(debounce(() => {
                        this.saveUndoAction({
                            chartIndex: this.currentRef?.chartIndex,
                            val: this.currentRef?.drawingRef,
                            data: this.currentUndoValue
                        })
                    }))
                }
            }
        }
    },
    created() {
        this.updateSettingsState(this)
    }
}
</script>
